import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {PersonEntity} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {usePersonAction} from "./usePersonAction";

const ALL_PERSON_ACTIONS = [
  "EDIT",
  "DELETE",
] as const;

export type PersonAction = typeof ALL_PERSON_ACTIONS[number];

export type PersonActionMenuDropdownProps = {
  person: PersonEntity
  actions?: PersonAction[]
  onActionClick?: (action: PersonAction) => void
  showEmpty?: boolean
}

export const PersonActionMenuDropdown = (props: PersonActionMenuDropdownProps) => {
  const {
    person,
    actions = ALL_PERSON_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<PersonAction> = useMemo(() => {
    const enabledActions = new Set<PersonAction>()
    const actionsSet = actions ? new Set<PersonAction>(actions) : new Set<PersonAction>()

    if (hasAccess("person_edit", `person_${person.type.code.toLowerCase()}_edit`)) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("person_delete", `person_${person.type.code.toLowerCase()}_delete`)) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [person, actions, hasAccess])

  const {
    onPersonActionClick
  } = usePersonAction()

  const _onActionClick = useCallback((action: PersonAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onPersonActionClick(person, action)
    }
  }, [onActionClick, onPersonActionClick, person])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
